import { Grid, makeStyles } from '@material-ui/core'
import DazzedHeading18 from 'components/common/text/DazzedHeading18'
import { TrackStatusColors, TxTrackingListTypes, getTrackingReasonColor } from 'consts/txTrackingReportConsts'
import React, { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { trackEvent } from 'utils/analyticsUtils'
import DateFilter from '../components/CopilotControlHeader/DateFilter'
import CopilotReportsNav from '../OralHygieneReport/CopilotReportsNav'
import OralHygieneNumberCard from '../OralHygieneReport/widgets/OralHygieneNumberCard'
import { CopilotReports } from '../hooks/useCopilotReports'
import useTxTrackingReport from './hooks/useTxTrackingReport'
import TxTrackingPatientsList from './patientsList/TxTrackingPatientsList'
import { getTxTrackingCategories } from './txTrackingReportLogic'
import TxTrackingChartWidget from './widgets/TxTrackingChartWidget'
import NotOnTrackStreakIcon from './widgets/icons/NotOnTrackStreakIcon'

// Figma frame 220 bar spacing, center to center: two status bars, and the reasons chart's four per page.
const TRACK_STATUS_BAR_SPACING = 102
const TRACKING_REASONS_BAR_SPACING = 74
const TRACKING_REASONS_PAGE_SIZE = 4

const useStyles = makeStyles(() => ({
  // Same page frame as the Oral Hygiene report: the reports rail at the far left, the report content fills the rest.
  page: {
    background: 'var(--copilot-bg-color)',
    borderTop: '1px solid #E6E6E6',
    boxSizing: 'border-box',
    width: '100%',
    height: 'calc(100% - var(--app-header-v2-height))',
    display: 'flex',
    flexDirection: 'row'
  },
  // Same header and columns as the Copilot and Oral Hygiene reports, so the title, date filter and
  // cards stay in place when switching reports from the rail.
  root: {
    flexGrow: 1,
    minWidth: 0,
    height: '100%',
    padding: '0px 40px',
    display: 'flex',
    flexDirection: 'column'
  },
  header: {
    flexShrink: 0,
    marginTop: 15,
    height: 'var(--copilot-header-height)',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start'
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    paddingLeft: 8
  },
  title: {
    fontWeight: 600
  },
  dateFilterContainer: {
    marginLeft: 5,
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'nowrap'
  },
  // Frame 220 stacks the left cards with 18px gaps and ends them 32px below the list card; the
  // column keeps the Oral Hygiene 10px gaps instead, so both columns end together.
  widgetsContainer: {
    marginTop: 20,
    flexGrow: 1,
    minHeight: 0,
    paddingBottom: 20
  },
  // The cards have a fixed minimum, so on short viewports the column scrolls instead of the charts
  // silently clipping their legends.
  leftColumn: {
    height: '100%',
    overflowY: 'auto'
  },
  chartWidgetContainer: {
    flex: '1 1 0',
    minHeight: 240,
    display: 'flex',
    flexDirection: 'column',
    '@media (max-width: 1200px)': {
      minHeight: 300
    }
  },
  numberCardContainer: {
    flexShrink: 0
  },
  listContainer: {
    height: '100%'
  }
}))

const TxTrackingReport = () => {
  const classes = useStyles()
  const { t } = useTranslation()

  const { accountOwnerId, pastXDays, setPastXDays, aggregationData, isAggregationLoading, isAggregationError } =
    useTxTrackingReport()

  const [selectedListType, setSelectedListType] = useState(TxTrackingListTypes.TrackingReasons)
  const [selectedCategories, setSelectedCategories] = useState([])

  const handleListTypeSelected = useCallback(
    listType => {
      trackEvent('Tx Tracking Report - Patient view changed', { view: listType, pastXDays })
      setSelectedListType(listType)
      // Switching views clears the category filters of the newly opened view.
      setSelectedCategories([])
    },
    [pastXDays]
  )

  const handleSeePatientsClicked = useCallback(
    (widget, listType) => {
      trackEvent('Tx Tracking Report - Widget - See Patients Clicked', { widget, view: listType, pastXDays })
      setSelectedListType(listType)
      // Widgets open the complete patient view without preselecting a category.
      setSelectedCategories([])
    },
    [pastXDays]
  )

  const handlePastXDaysChanged = useCallback(
    updatedPastXDays => {
      trackEvent('Tx Tracking Report - Time range clicked', { time: updatedPastXDays, view: selectedListType })
      // Changing the date keeps the selected patient view and category filters.
      setPastXDays(updatedPastXDays)
    },
    [setPastXDays, selectedListType]
  )

  const trackStatusBars = useMemo(
    () =>
      getTxTrackingCategories(aggregationData, TxTrackingListTypes.TrackStatus).map(category => ({
        key: category.key,
        name: t(`pages.txTrackingReport.patientsList.categories.${category.key}`),
        value: category.count,
        color: TrackStatusColors[category.key]
      })),
    [aggregationData, t]
  )

  const trackingReasonBars = useMemo(
    () =>
      getTxTrackingCategories(aggregationData, TxTrackingListTypes.TrackingReasons).map(category => ({
        key: category.key,
        name: t(`dialogs.scanSummary.treatmentTracking.conditions.${category.key}`),
        value: category.count,
        color: getTrackingReasonColor(category.key)
      })),
    [aggregationData, t]
  )

  return (
    <div className={classes.page}>
      <CopilotReportsNav activeReport={CopilotReports.TxTracking} />
      <div className={classes.root}>
        <div className={classes.header}>
          <div className={classes.titleRow}>
            <DazzedHeading18 className={classes.title}>{t('pages.txTrackingReport.mainPage.title')}</DazzedHeading18>
          </div>
          <Grid container className={classes.dateFilterContainer}>
            <Grid item>
              <DateFilter pastXDays={pastXDays} onChange={handlePastXDaysChanged} />
            </Grid>
          </Grid>
        </div>

        <Grid container id="tx-tracking-widgets" wrap="nowrap" className={classes.widgetsContainer}>
          <Grid container direction="column" item xs={4} wrap="nowrap" className={classes.leftColumn}>
            <Grid item className={classes.chartWidgetContainer}>
              <TxTrackingChartWidget
                title={t('pages.txTrackingReport.widgets.trackStatus.title')}
                tooltip={t('pages.txTrackingReport.widgets.trackStatus.tooltip')}
                bars={trackStatusBars}
                barSpacing={TRACK_STATUS_BAR_SPACING}
                isLoading={isAggregationLoading}
                isError={isAggregationError}
                isSelected={selectedListType === TxTrackingListTypes.TrackStatus}
                onSeePatients={() => handleSeePatientsClicked('trackStatus', TxTrackingListTypes.TrackStatus)}
              />
            </Grid>
            <Grid item className={classes.chartWidgetContainer}>
              <TxTrackingChartWidget
                title={t('pages.txTrackingReport.widgets.trackingReasons.title')}
                tooltip={t('pages.txTrackingReport.widgets.trackingReasons.tooltip')}
                bars={trackingReasonBars}
                barSpacing={TRACKING_REASONS_BAR_SPACING}
                pageSize={TRACKING_REASONS_PAGE_SIZE}
                isLoading={isAggregationLoading}
                isError={isAggregationError}
                isSelected={selectedListType === TxTrackingListTypes.TrackingReasons}
                onSeePatients={() => handleSeePatientsClicked('trackingReasons', TxTrackingListTypes.TrackingReasons)}
                onPageChanged={page =>
                  trackEvent('Tx Tracking Report - Reasons chart page changed', { page, pastXDays })
                }
              />
            </Grid>
            <Grid item className={classes.numberCardContainer}>
              <OralHygieneNumberCard
                label={t('pages.txTrackingReport.widgets.notOnTrackStreak.title')}
                icon={<NotOnTrackStreakIcon />}
                value={aggregationData?.notOnTrackStreak || 0}
                isLoading={isAggregationLoading}
                isError={isAggregationError}
                isSelected={selectedListType === TxTrackingListTypes.NotOnTrackStreak}
                onSeePatients={() => handleSeePatientsClicked('notOnTrackStreak', TxTrackingListTypes.NotOnTrackStreak)}
              />
            </Grid>
          </Grid>
          <Grid container direction="column" item xs={8} wrap="nowrap" className={classes.listContainer}>
            <Grid item style={{ flexGrow: 1, height: '100%' }}>
              <TxTrackingPatientsList
                accountOwnerId={accountOwnerId}
                listType={selectedListType}
                onListTypeSelected={handleListTypeSelected}
                selectedCategories={selectedCategories}
                setSelectedCategories={setSelectedCategories}
                pastXDays={pastXDays}
                aggregationData={aggregationData}
                isAggregationLoading={isAggregationLoading}
              />
            </Grid>
          </Grid>
        </Grid>
      </div>
    </div>
  )
}

export default TxTrackingReport
